import { useCallback, useMemo, useState } from 'react'
import useUserStore from '@/user/userStore'
import useAuthStore from '@/auth/authStore'
import { useLocalFlags } from '@/common/featureFlags/local/useLocalFlags'
import { getValidTrustedDevice } from '@/auth/features/bff-auth/trustedDevice/trustedDeviceStorage'
import { NAVIGATION_COPY } from '@/locales/en'
import { buildProfileDetailItems } from './profileDetails'
import type { ProfileDetailItem } from '../types'

interface UseProfileDetailItemsProps {
    onEditPassword: () => void
    onEditMfa: () => void
    onForgetDevice: () => Promise<void>
}

export function useProfileDetailItems({ onEditPassword, onEditMfa, onForgetDevice }: UseProfileDetailItemsProps) {
    const { user } = useUserStore()
    const { authDetails } = useAuthStore()
    const { showBffAuth, showOnboardingV2 } = useLocalFlags()
    const [hasTrustedDevice, setHasTrustedDevice] = useState(
        () => !!user?.emailId && getValidTrustedDevice(user.emailId) !== null,
    )

    // If user logged in via Google, username would be something like Google_12XXX7X99
    // If user logged in via Email, and password, username would be something like username@email.com
    // So we are checking that if username Starts with Google,
    // and we are also checking if it does not includes @ to avoid considering emails like googleuser@email.com as Google sign in
    const signedInViaGoogle = authDetails
        ? authDetails.username.toLowerCase().startsWith('google') && !authDetails.username.includes('@')
        : false

    const bffAuthEnabled = Boolean(showBffAuth) && Boolean(showOnboardingV2)
    const mfaEnabled = Boolean(user?.mfaEnabled)
    const mfaCopy = NAVIGATION_COPY.account.mfaFields
    const trustedDeviceCopy = NAVIGATION_COPY.account.trustedDeviceFields

    const handleForgetDevice = useCallback(async () => {
        await onForgetDevice()
        setHasTrustedDevice(!!user?.emailId && getValidTrustedDevice(user.emailId) !== null)
    }, [onForgetDevice, user?.emailId])

    const items: ProfileDetailItem[] = useMemo(
        () =>
            user
                ? buildProfileDetailItems(user, {
                      signedInViaGoogle,
                      bffAuthEnabled,
                      mfaEnabled,
                      mfaCopy,
                      hasTrustedDevice,
                      trustedDeviceCopy,
                      onEditPassword,
                      onEditMfa,
                      onForgetDevice: handleForgetDevice,
                  })
                : [],
        [
            user,
            signedInViaGoogle,
            bffAuthEnabled,
            mfaEnabled,
            mfaCopy,
            hasTrustedDevice,
            trustedDeviceCopy,
            onEditPassword,
            onEditMfa,
            handleForgetDevice,
        ],
    )

    return { user, items }
}
